import React, { useEffect, useState } from "react";
import { getAllByProgramIdAndGroupName } from "../../services/tripsService";
import { confirmPassengerTrip } from "../../services/passengerService";
import { processErrorResponse } from "../../helpers/processErrorResponse";
import { primary } from "../../ui/colors";

const primaryLight = "#e0faf7";
const primaryDark = "#00a896";

// Same trip list as the "Elegir programa" select in the registration flow.
const normalizeTrips = (res) =>
  (res || [])
    .map((t) => ({ id: t.tripId, name: t.tripName }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }));

const TripConfirmation = ({
  passengerId,
  programId,
  groupName,
  currentTripId,
  status,
  requestedTripName,
  onConfirmed,
}) => {
  const isPending = status === "Pending";
  const [options, setOptions] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(
    currentTripId != null ? String(currentTripId) : ""
  );
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState();

  useEffect(() => {
    if (!isPending || programId == null || !groupName) return;
    setIsLoadingOptions(true);
    getAllByProgramIdAndGroupName(programId, groupName)
      .then((res) => setOptions(normalizeTrips(res)))
      .catch((e) => setError(processErrorResponse(e)?.[1] ?? "Ocurrio un error"))
      .finally(() => setIsLoadingOptions(false));
  }, [isPending, programId, groupName]);

  const handleConfirm = () => {
    const isCurrentTrip = selectedTripId === String(currentTripId);
    setIsSubmitting(true);
    setError();
    confirmPassengerTrip(
      passengerId,
      isCurrentTrip ? undefined : Number(selectedTripId)
    )
      // Reload the passenger trips so the card shows the updated status.
      .then(() => onConfirmed?.())
      .catch((e) => setError(processErrorResponse(e)?.[1] ?? "Ocurrio un error"))
      .finally(() => setIsSubmitting(false));
  };

  if (!isPending) {
    if (!status) return null;
    const isChangeRequested = status === "ChangeRequested";
    const label = isChangeRequested ? "Cambio solicitado" : "Destino confirmado";
    return (
      <div
        style={{
          ...styles.result,
          ...(isChangeRequested ? styles.resultChangeRequested : {}),
        }}
      >
        {label}
        {requestedTripName ? `: ${requestedTripName}` : ""}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <p style={styles.text}>
        Tu destino esta pendiente de confirmacion, por favor, confirmar el
        destino o elegir uno distinto
      </p>

      <select
        style={styles.select}
        value={selectedTripId}
        onChange={(e) => setSelectedTripId(e.target.value)}
        disabled={isLoadingOptions || isSubmitting}
      >
        {isLoadingOptions && <option value={selectedTripId}>Cargando...</option>}
        {!isLoadingOptions &&
          options.map((o) => (
            <option key={o.id} value={String(o.id)}>
              {String(o.id) === String(currentTripId)
                ? `Sigo en mi programa (${o.name})`
                : o.name}
            </option>
          ))}
      </select>

      {selectedTripId &&
        !isLoadingOptions &&
        selectedTripId !== String(currentTripId) && (
          <p style={styles.changeWarning}>
            Estás solicitando un cambio de programa. La solicitud va a ser
            evaluada y comunicaremos los detalles para hacer efectivo el
            cambio.
          </p>
        )}

      {selectedTripId && !isLoadingOptions && (
        <button
          style={{ ...styles.button, opacity: isSubmitting ? 0.6 : 1 }}
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Cargando..." : "CONFIRMAR"}
        </button>
      )}

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

export default TripConfirmation;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    background: "#fff8e6",
    border: "1px solid #f5c26b",
  },
  text: {
    margin: 0,
    fontSize: 13,
    color: "#7a5200",
  },
  select: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1.5px solid #e0e0e0",
    fontSize: 14,
    fontFamily: "Gilroy",
    background: "#fff",
  },
  button: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 8,
    background: primary,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Gilroy-Bold",
  },
  result: {
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    background: primaryLight,
    color: primaryDark,
    border: `1px solid ${primary}`,
  },
  resultChangeRequested: {
    background: "#fff8e6",
    color: "#7a5200",
    border: "1px solid #f5c26b",
  },
  changeWarning: {
    margin: 0,
    fontSize: 13,
    color: "#c0392b",
    fontWeight: 600,
  },
  error: {
    fontSize: 12,
    color: "#c0392b",
  },
};
